/**
 * Die **Abdeckung**, je Agent und Zeitfenster — an der Hauptansicht und nicht in
 * der Verwaltung (Spec v1.3, Story 230; ADR-0021).
 *
 * Sie ist die Zusage, mit der dieses Werkzeug beschrieben wird: „Wenn es läuft,
 * liegt von jedem aktiven Agenten eine vollständige Begutachtung aller
 * Revisionen vor." Eine Abdeckung, auf die man sich verlässt und die still bei
 * 94 Prozent liegt, ist schädlicher als gar keine — deshalb steht sie über dem
 * Revisionsstrom und nicht hinter einem Menüpunkt.
 *
 * **Vier Fenster statt einer Aufteilung nach Projekt** (ADR-0021). Der Preis ist
 * benannt: Eine Lücke, die nur ein Projekt betrifft, ist hier nicht mehr als
 * Projektlücke zu erkennen. Was die Fenster dafür zeigen, ist der Stillstand —
 * ein Agent, der seit gestern nichts abarbeitet, fällt in „Seit Beginn" um
 * Bruchteile und in „24 Stunden" sofort auf.
 *
 * **Die Zelle ist zweizeilig**: das farbige Schild mit dem Anteil oben, die
 * beiden Zahlen klein und gedämpft darunter — wie `FindingCell` es in derselben
 * Anwendung vormacht. Der Anteil ist die Aussage, die Zahlen sind der Beleg, und
 * der Anteil steht nie ohne sie.
 *
 * **Leerer Nenner ist ein Gedankenstrich** und keine hundert Prozent, und zwar je
 * Zelle: Ein Agent, der in den letzten 24 Stunden zu keiner Revision beauftragt
 * war, hat dort nichts abgedeckt — dieselbe Schreibweise, die auch die
 * Abweichungskennzahl bei zu kleiner Fallzahl wählt.
 *
 * **Die Zeile „mindestens ein Agent" steht zuoberst und abgesetzt.** Abgesetzt
 * wird sie im Inhalt ihrer Zellen — starker Name mit Zeichen und stärkere
 * Beleglinie —, statt in die Innereien des Bausteins zu greifen oder eine zweite
 * Tabelle mit zweitem Kopf daneben zu stellen. Der `tone` je Zeile, den der
 * Baustein seit #142 führt, ist hier bewusst nicht gesetzt: Er färbt die ganze
 * Zeile und sagt „ok/offen/überfällig" — eine Zusammenfassung ist keines davon.
 *
 * **Gefragt wird in einem eigenen, langsamen Takt von 60 Sekunden**, nicht nur
 * einmal beim Erscheinen: Rollende Fenster veralten, und eine offene Seite zeigte
 * sonst nach zwei Stunden ein Bild von vor zwei Stunden. Nicht im
 * Fünf-Sekunden-Takt des Stroms — das wären zwölfmal so viele Apache-Prozesse für
 * eine Zahl, die sich so schnell nicht bewegt. Der Takt hält an, wenn das Fenster
 * im Hintergrund ist, wie der des Stroms.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";

import { Chip, DataTable, Icon, StatusPill } from "../design-system";
import type {
  AgentCompletion,
  Completion,
  CompletionApi,
  CompletionTally,
  CompletionWindows,
} from "../api/client";
import { formatNumber, formatShare } from "../formatting";
import { StrongCell } from "./cells";
import type { Tone } from "./findings";
import { ListSection } from "./list";
import { Notice, describe } from "./loading";

/**
 * Ab wann die Abdeckung auffällig ist.
 *
 * Zwei Schwellen und keine Ampel mit fünf Stufen: Es geht um die Frage „kann ich
 * mich auf die Zahl verlassen", und die hat drei Antworten. Vollständig ist
 * ausdrücklich erst die Eins — bei 99,6 Prozent fehlt etwas, und genau das soll
 * zu sehen sein.
 */
const NEARLY = 0.9;

/** Der eigene Takt der Karte, weit langsamer als der des Revisionsstroms. */
const POLL_MILLISECONDS = 60_000;

const HEADING = "Abdeckung";

const EMPTY = "Kein Agent angelegt — es gibt nichts abzudecken.";

const ANY_AGENT = "mindestens ein Agent";

/** Was die Zahl bedeutet, ausgeschrieben — im Titel, nicht in der Zeile. */
const SHARE_TITLE =
  "Der Anteil der Revisionen, zu denen dieser Agent beauftragt ist und bei denen jeder " +
  "Auftrag erledigt ist — rückwirkend Beauftragtes zählt mit. Die Synthese gehört dazu; " +
  "die Kontrolle nicht. Die Version des Grundtexts geht nicht ein.";

const ANY_AGENT_TITLE =
  "Der Anteil der Revisionen, zu denen überhaupt ein Prüfauftrag entstanden ist und bei " +
  "denen mindestens ein Agent vollständig durch ist. Diese Zeile zählt alle Agenten mit, " +
  "auch stillgelegte: Sie ist eine Aussage über die Revisionen und nicht über den Betrieb.";

const NOTHING_RESPONSIBLE =
  "In diesem Zeitfenster ist zu keiner Revision ein Auftrag entstanden — keine Lücke, " +
  "sondern nichts, worüber sich eine Zahl aussagen ließe.";

/** Die vier Fenster als Spalten, in der Reihenfolge des kürzesten zuerst. */
const WINDOWS: { key: keyof CompletionWindows; label: string }[] = [
  { key: "last24Hours", label: "24 Stunden" },
  { key: "last7Days", label: "7 Tage" },
  { key: "last30Days", label: "30 Tage" },
  { key: "sinceStart", label: "Seit Beginn" },
];

export function CompletionSection({ client }: { client: CompletionApi }): ReactElement {
  const state = usePolled(client);
  const completion = state.kind === "loaded" ? state.value : null;
  const agents = completion === null ? [] : completion.agents;
  const lowest = lowestOf(agents);

  return (
    <ListSection
      title={HEADING}
      glyph="target"
      first
      headerRight={agents.length === 0 ? null : <LowestShare share={lowest} />}
    >
      <div style={{ padding: "var(--sp-2) var(--sp-5)" }}>
        {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
        {state.kind === "failed" ? (
          <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
        ) : null}
        {completion !== null && agents.length === 0 ? <Notice text={EMPTY} /> : null}
      </div>

      {completion === null || agents.length === 0 ? null : (
        <DataTable
          columns={[
            { key: "agent", label: "Agent", maxWidth: 260 },
            ...WINDOWS.map((window) => ({ key: window.key, label: window.label, width: 150 })),
          ]}
          rows={[
            {
              cells: {
                agent: (
                  <StrongCell>
                    <AnyAgentName />
                  </StrongCell>
                ),
                ...cellsOf(completion.anyAgent, ANY_AGENT_TITLE, true),
              },
            },
            ...agents.map((agent) => ({
              cells: {
                agent: <StrongCell>{agent.agentName}</StrongCell>,
                ...cellsOf(agent, SHARE_TITLE, false),
              },
            })),
          ]}
        />
      )}
    </ListSection>
  );
}

/**
 * Die vier Zellen einer Zeile.
 *
 * @returns die Fensterschlüssel auf ihre Zelle — so, wie `DataTable` sie sucht
 */
function cellsOf(
  windows: CompletionWindows,
  shareTitle: string,
  summary: boolean,
): Record<string, ReactNode> {
  const cells: Record<string, ReactNode> = {};

  for (const window of WINDOWS) {
    cells[window.key] = (
      <ShareCell tally={windows[window.key]} shareTitle={shareTitle} summary={summary} />
    );
  }

  return cells;
}

/**
 * Der Anteil eines Fensters über seinen beiden Zahlen, farbig nach dem, was er
 * über die Zusage sagt.
 *
 * `null` ist nicht Null: Es heißt, dass in diesem Fenster zu keiner Revision ein
 * Auftrag entstanden ist — und das ist keine Lücke, sondern eine leere
 * Grundgesamtheit.
 */
function ShareCell({
  tally,
  shareTitle,
  summary,
}: {
  tally: CompletionTally;
  shareTitle: string;
  summary: boolean;
}): ReactElement {
  const share = shareOf(tally);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "var(--sp-3)",
      }}
    >
      {share === null ? (
        <span style={{ color: "var(--fg-6)" }} title={NOTHING_RESPONSIBLE}>
          —
        </span>
      ) : (
        <span title={shareTitle}>
          <StatusPill tone={toneOf(share)}>{formatShare(share)}</StatusPill>
        </span>
      )}
      <span
        style={{
          fontSize: "var(--fs-11)",
          color: summary ? "var(--fg-3)" : "var(--fg-6)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatNumber(tally.complete)} von {formatNumber(tally.responsible)}
      </span>
    </div>
  );
}

/** Der Name der abgesetzten Zeile — mit Zeichen, weil sie keine Agentenzeile ist. */
function AnyAgentName(): ReactElement {
  return (
    <span
      title={ANY_AGENT_TITLE}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--sp-4)",
        color: "var(--fg-1)",
        fontWeight: "var(--fw-semibold)",
      }}
    >
      <Icon name="layers" size={14} />
      {ANY_AGENT}
    </span>
  );
}

/** `null`, solange kein Nenner dasteht — geteilt wird durch Null nicht. */
function shareOf(tally: CompletionTally): number | null {
  return tally.responsible === 0 ? null : tally.complete / tally.responsible;
}

/**
 * Der niedrigste Anteil der Agentenzeilen in „Seit Beginn" — das, was im Kopf der
 * Karte steht.
 *
 * Ein Mittelwert über alle Agenten verwischte genau den Fall, für den die Zahl
 * gebaut ist: ein Agent, der stillsteht, während die übrigen mitlaufen. Zeilen
 * mit leerem Nenner gehen nicht ein, und die Zeile „mindestens ein Agent" auch
 * nicht — sie ist eine andere Aussage.
 */
function lowestOf(agents: AgentCompletion[]): number | null {
  const shares: number[] = [];

  for (const agent of agents) {
    const share = shareOf(agent.sinceStart);

    if (share !== null) {
      shares.push(share);
    }
  }

  return shares.length === 0 ? null : Math.min(...shares);
}

/**
 * Die Farbe eines Standes — und `null`, wo es keinen gibt.
 *
 * Kein Stand ist kein Zustand: Wer für nichts zuständig ist, steht nicht schlecht
 * da, er steht gar nicht da. Das war unter `Badge` der Ton `neutral` und ist
 * seit #141 die farblose Pille daneben.
 *
 * Zwei Signaturen, weil beide Aufruforte gebraucht werden: Der Kopf des
 * Abschnitts kennt den Fall ohne Stand, die Zelle hat ihn schon abgefangen und
 * bekäme sonst ein `null`, das dort nicht vorkommen kann.
 */
function toneOf(share: number): Tone;
function toneOf(share: number | null): Tone | null;
function toneOf(share: number | null): Tone | null {
  if (share === null) {
    return null;
  }

  if (share >= 1) {
    return "green";
  }

  return share >= NEARLY ? "amber" : "red";
}

/**
 * Der niedrigste Stand über alle Agenten, im Kopf des Abschnitts.
 *
 * Ohne Stand steht dort ein Gedankenstrich — und der ist kein Zustand, sondern
 * die Auskunft, dass niemand zuständig war.
 */
function LowestShare({ share }: { share: number | null }): ReactElement {
  const tone = toneOf(share);
  const label = share === null ? "—" : `mindestens ${formatShare(share)}`;

  return tone === null ? <Chip>{label}</Chip> : <StatusPill tone={tone}>{label}</StatusPill>;
}

type Polled =
  | { kind: "loading" }
  | { kind: "loaded"; value: Completion }
  | { kind: "failed"; message: string };

/**
 * Fragt beim Erscheinen und danach im eigenen Takt nach.
 *
 * Er ruht, solange das Fenster im Hintergrund liegt: Eine offene, unbeachtete
 * Registerkarte bindet sonst rund um die Uhr einen Apache-Prozess je Minute für
 * ein Bild, das niemand ansieht.
 */
function usePolled(client: CompletionApi): Polled {
  const [state, setState] = useState<Polled>({ kind: "loading" });
  const latest = useRef(client);
  latest.current = client;

  useEffect(() => {
    let discarded = false;

    const ask = (): void => {
      latest.current.completion().then(
        (value) => {
          if (!discarded) {
            setState({ kind: "loaded", value });
          }
        },
        (error: unknown) => {
          if (!discarded) {
            setState({ kind: "failed", message: describe(error) });
          }
        },
      );
    };

    ask();

    const tick = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        ask();
      }
    }, POLL_MILLISECONDS);

    return () => {
      discarded = true;
      window.clearInterval(tick);
    };
  }, [client]);

  return state;
}
